import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Prospects API
export const prospectsApi = {
  getAll: (params = {}) => axios.get(`${API}/prospects`, { params }),
  getOne: (id) => axios.get(`${API}/prospects/${id}`),
  getMine: () => axios.get(`${API}/prospects/me`),
  create: (data) => axios.post(`${API}/prospects`, data),
  update: (id, data) => axios.put(`${API}/prospects/${id}`, data),
  delete: (id) => axios.delete(`${API}/prospects/${id}`),
  getAllAdmin: () => axios.get(`${API}/prospects/all`),
  search: (query) => axios.get(`${API}/search/prospects`, { params: { q: query } }),
  compare: (ids) => axios.get(`${API}/prospects/compare`, { params: { ids: ids.join(',') } }),
  getIntelligence: (id) => axios.get(`${API}/prospects/${id}/intelligence`),
  // Highlight links
  getHighlightLinks: (prospectId, params) => axios.get(`${API}/prospects/${prospectId}/highlights`, params ? { params } : undefined),
  createHighlightLink: (prospectId, data) => axios.post(`${API}/prospects/${prospectId}/highlights`, data),
  updateHighlightLink: (prospectId, linkId, data) => axios.put(`${API}/prospects/${prospectId}/highlights/${linkId}`, data),
  deleteHighlightLink: (prospectId, linkId) => axios.delete(`${API}/prospects/${prospectId}/highlights/${linkId}`),
  // Admin highlight management
  getPendingHighlights: () => axios.get(`${API}/admin/highlights/pending`),
  bulkHighlightAction: (link_ids, action) => axios.post(`${API}/admin/highlights/bulk`, { link_ids, action }),
  resendApprovalEmail: (prospectId) => axios.post(`${API}/prospects/${prospectId}/resend-approval-email`),
  // Phase 7P — staff denial of a pending prospect profile (with optional reason)
  denyProspect: (prospectId, reason = null) =>
    axios.post(`${API}/prospects/${prospectId}/deny`, reason ? { reason } : {}),
  // Phase 7P — current prospect's denial state (returns {denied_at: string|null})
  getMyDenialStatus: () => axios.get(`${API}/prospects/me/denial-status`),
  // PX-3 — request a re-evaluation from FO staff (no rating change)
  requestReevaluation: () => axios.post(`${API}/prospects/me/request-reevaluation`),
  // PX-4 — FO/admin marks a re-evaluation request reviewed
  markReevaluationReviewed: (prospectId) =>
    axios.post(`${API}/admin/prospects/${prospectId}/mark-reevaluation-reviewed`),
  // Phase 7R — self-service permanent account deletion (prospect only)
  deleteMyAccount: () => axios.delete(`${API}/prospects/me/account`),
};

// Scouting API
export const scoutingApi = {
  getReports: (prospectId) => axios.get(`${API}/scouting-reports/prospect/${prospectId}`),
  createReport: (data) => axios.post(`${API}/scouting-reports`, data),
  updateReport: (id, data) => axios.put(`${API}/scouting-reports/${id}`, data),
  deleteReport: (id) => axios.delete(`${API}/scouting-reports/${id}`),
  getCategories: () => axios.get(`${API}/scouting-reports/categories`),
};

// Comparisons API
export const comparisonsApi = {
  get: (prospectId) => axios.get(`${API}/prospect-comparisons/${prospectId}`),
  create: (data) => axios.post(`${API}/prospect-comparisons`, data),
  delete: (id) => axios.delete(`${API}/prospect-comparisons/${id}`),
};

// Rankings API
export const rankingsApi = {
  getAll: (params = {}) => axios.get(`${API}/prospect-rankings`, { params }),
  create: (data) => axios.post(`${API}/prospect-rankings`, data),
  update: (id, data) => axios.put(`${API}/prospect-rankings/${id}`, data),
  delete: (id) => axios.delete(`${API}/prospect-rankings/${id}`),
};

// File Upload API
export const uploadApi = {
  uploadHeadshot: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API}/upload/headshot`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getFileUrl: (fileId) => `${API}/files/${fileId}`,
};

// Teams API
export const teamsApi = {
  getAll: (params) => axios.get(`${API}/teams`, { params }),
  getOne: (id) => axios.get(`${API}/teams/${id}`),
  getRoster: (id) => axios.get(`${API}/teams/${id}/roster`),
  getDraftPicks: (id, params) => axios.get(`${API}/teams/${id}/draft-picks`, { params }),
  getHighlights: (id, limit) => axios.get(`${API}/teams/${id}/highlights`, { params: { limit } }),
  createHighlight: (id, data) => axios.post(`${API}/teams/${id}/highlights`, data),
  deleteHighlight: (id) => axios.delete(`${API}/highlights/${id}`),
  create: (data) => axios.post(`${API}/teams`, data),
  update: (id, data) => axios.put(`${API}/teams/${id}`, data),
  delete: (id) => axios.delete(`${API}/teams/${id}`),
  getCoachingStaff: (id) => axios.get(`${API}/teams/${id}/coaching-staff`),
  getFranchiseProgress: (id) => axios.get(`${API}/teams/${id}/franchise-progress`),
  getMomentum: (id) => axios.get(`${API}/teams/${id}/momentum`),
  getSelfSufficiency: (id) => axios.get(`${API}/teams/${id}/self-sufficiency`),
  getMoneyPot: (id) => axios.get(`${API}/teams/${id}/money-pot`),
};

// FRANCHISE-CATALOG-SOT-1B — Franchise Level Catalog (name + description).
// Public/read-only. Backed by public.franchise_levels (Migration 053).
export const franchiseCatalogApi = {
  getLevelsCatalog: () => axios.get(`${API}/franchise/levels-catalog`),
};

// Phase MP-2 — Money Pot Approval Workflow
export const moneyPotApi = {
  // Team Staff + Front Office
  submitEntry: (teamId, data) =>
    axios.post(`${API}/teams/${teamId}/money-pot/entries`, data),
  getMyEntries: (teamId, params = {}) =>
    axios.get(`${API}/teams/${teamId}/money-pot/entries/mine`, { params }),
  getTeamEntries: (teamId, params = {}) =>
    axios.get(`${API}/teams/${teamId}/money-pot/entries`, { params }),
  // Front Office only
  getPendingQueue: () => axios.get(`${API}/money-pot/entries/pending`),
  approveEntry: (entryId) =>
    axios.post(`${API}/money-pot/entries/${entryId}/approve`),
  rejectEntry: (entryId, rejection_reason) =>
    axios.post(`${API}/money-pot/entries/${entryId}/reject`, { rejection_reason }),
  editEntry: (entryId, data) =>
    axios.put(`${API}/money-pot/entries/${entryId}`, data),
  deleteEntry: (entryId) =>
    axios.delete(`${API}/money-pot/entries/${entryId}`),
  // Phase MP-3 — Front Office Command Center (FO-only)
  getCommandCenter: () => axios.get(`${API}/money-pot/command-center`),
  getTeamDetail:    (teamId, params = {}) =>
    axios.get(`${API}/money-pot/team-detail/${teamId}`, { params }),
  // Phase MP-5 — Project management
  listProjects: (teamId, params = {}) =>
    axios.get(`${API}/teams/${teamId}/money-pot/projects`, { params }),
  createProject: (teamId, data) =>
    axios.post(`${API}/teams/${teamId}/money-pot/projects`, data),
  updateProject: (projectId, data) =>
    axios.put(`${API}/money-pot/projects/${projectId}`, data),
  deleteProject: (projectId) =>
    axios.delete(`${API}/money-pot/projects/${projectId}`),
  // Phase MP-10 — Project Update submissions
  submitProjectUpdate: (teamId, data) =>
    axios.post(`${API}/teams/${teamId}/money-pot/project-updates`, data),
  listMyProjectUpdates: (teamId, params = {}) =>
    axios.get(`${API}/teams/${teamId}/money-pot/project-updates/mine`, { params }),
  listTeamProjectUpdates: (teamId, params = {}) =>
    axios.get(`${API}/teams/${teamId}/money-pot/project-updates`, { params }),
  // Phase MP-5 closure — FO project-update approval workflow
  listPendingProjectUpdates: () =>
    axios.get(`${API}/money-pot/project-updates/pending`),
  approveProjectUpdate: (updateId) =>
    axios.post(`${API}/money-pot/project-updates/${updateId}/approve`),
  rejectProjectUpdate: (updateId, rejection_reason) =>
    axios.post(`${API}/money-pot/project-updates/${updateId}/reject`, { rejection_reason }),
  editProjectUpdate: (updateId, data) =>
    axios.put(`${API}/money-pot/project-updates/${updateId}`, data),
  // Phase MP-8 — Team Growth Map + League Comparison
  getLeagueComparison: () =>
    axios.get(`${API}/money-pot/league-comparison`),
  getTeamTimeline: (teamId) =>
    axios.get(`${API}/money-pot/team-timeline/${teamId}`),
  // FL1 — Project detail capability probe + authorized link-option picker
  getProjectDetailsCapability: () =>
    axios.get(`${API}/money-pot/project-details-capability`),
  getProjectLinkOptions: (teamId) =>
    axios.get(`${API}/money-pot/project-link-options`, { params: { team_id: teamId } }),
  // FL1 — Completion evidence (Front Office only)
  getEvidenceCapability: () =>
    axios.get(`${API}/money-pot/project-evidence-capability`),
  listProjectEvidence: (projectId) =>
    axios.get(`${API}/money-pot/projects/${projectId}/evidence`),
  recordProjectEvidence: (projectId, data) =>
    axios.post(`${API}/money-pot/projects/${projectId}/evidence`, data),
  updateProjectEvidence: (evidenceId, data) =>
    axios.put(`${API}/money-pot/project-evidence/${evidenceId}`, data),
  verifyProjectEvidence: (evidenceId, data) =>
    axios.post(`${API}/money-pot/project-evidence/${evidenceId}/verify`, data),
  revokeProjectEvidence: (evidenceId, data) =>
    axios.post(`${API}/money-pot/project-evidence/${evidenceId}/revoke`, data),
};

export const rosterApi = {
  assign: (data) => axios.post(`${API}/admin/roster/assign`, data),
  remove: (prospectId) => axios.post(`${API}/admin/roster/remove?prospect_id=${prospectId}`),
};

export const standingsApi = {
  getAll: (params) => axios.get(`${API}/standings`, { params }),
};

// Games API
export const gamesApi = {
  getAll: (params = {}) => axios.get(`${API}/games`, { params }),
  getOne: (id) => axios.get(`${API}/games/${id}`),
  create: (data) => axios.post(`${API}/games`, data),
  update: (id, data) => axios.put(`${API}/games/${id}`, data),
  delete: (id) => axios.delete(`${API}/games/${id}`),
};

// Draft Pool API
export const draftPoolApi = {
  getAll: (params = {}) => axios.get(`${API}/draft-pool`, { params }),
  add: (data) => axios.post(`${API}/draft-pool`, data),
  approve: (id) => axios.put(`${API}/draft-pool/${id}/approve`),
  remove: (id) => axios.delete(`${API}/draft-pool/${id}`),
  // Admin endpoints
  getAllAdmin: (params = {}) => axios.get(`${API}/admin/draft-pool`, { params }),
  adminAdd: (prospectId, draftYear) => axios.post(`${API}/admin/draft-pool/add/${prospectId}?draft_year=${draftYear}`),
  adminRemove: (entryId) => axios.delete(`${API}/admin/draft-pool/${entryId}`),
  resetVotes: (draftYear, prospectId = null) => 
    axios.post(`${API}/admin/votes/reset`, null, { params: { draft_year: draftYear, prospect_id: prospectId } }),
  getVoteAnalytics: (draftYear) => axios.get(`${API}/admin/votes/analytics`, { params: { draft_year: draftYear } }),
};

// Mock Draft API
export const mockDraftApi = {
  getAll: (params = {}) => axios.get(`${API}/mock-drafts`, { params }),
  getMine: () => axios.get(`${API}/mock-drafts/mine`),
  getOne: (id) => axios.get(`${API}/mock-drafts/${id}`),
  create: (data) => axios.post(`${API}/mock-drafts`, data),
  delete: (id) => axios.delete(`${API}/mock-drafts/${id}`),
  like: (id) => axios.post(`${API}/mock-drafts/${id}/like`),
  unlike: (id) => axios.delete(`${API}/mock-drafts/${id}/like`),
};

// Votes API
export const votesApi = {
  cast: (data) => axios.post(`${API}/votes`, data),
  getMyVotes: (params = {}) => axios.get(`${API}/votes/my-votes`, { params }),
  remove: (id) => axios.delete(`${API}/votes/${id}`),
};

// Leaderboards API
export const leaderboardsApi = {
  getOverall: (limit = 10) => axios.get(`${API}/leaderboards/overall`, { params: { limit } }),
  getTopSpeed: (limit = 10) => axios.get(`${API}/leaderboards/top-speed`, { params: { limit } }),
  getFootballIQ: (limit = 10) => axios.get(`${API}/leaderboards/football-iq`, { params: { limit } }),
  getExplosiveOutput: (limit = 10) => axios.get(`${API}/leaderboards/explosive-output`, { params: { limit } }),
  getCompetitiveEdge: (limit = 10) => axios.get(`${API}/leaderboards/competitive-edge`, { params: { limit } }),
  getDraftVotes: (draftYear, limit = 10) => axios.get(`${API}/leaderboards/draft-votes`, { params: { draft_year: draftYear, limit } }),
};

// Announcements API
export const announcementsApi = {
  getAll: (limit = 5) => axios.get(`${API}/announcements`, { params: { limit } }),
  create: (data) => axios.post(`${API}/announcements`, data),
  delete: (id) => axios.delete(`${API}/announcements/${id}`),
};

// Stats API
export const statsApi = {
  getPublic: () => axios.get(`${API}/stats`),
  getAdmin: () => axios.get(`${API}/admin/stats`),
};

// Follow API
export const followApi = {
  followTeam: (teamId) => axios.post(`${API}/follow/team/${teamId}`),
  unfollowTeam: (teamId) => axios.delete(`${API}/follow/team/${teamId}`),
  getFollowedTeams: () => axios.get(`${API}/follow/teams`),
};

// Draft Events API
export const draftEventsApi = {
  getAll: (params = {}) => axios.get(`${API}/draft-events`, { params }),
  getOne: (id) => axios.get(`${API}/draft-events/${id}`),
  create: (data) => axios.post(`${API}/draft-events`, data),
  start: (id) => axios.put(`${API}/draft-events/${id}/start`),
  pause: (id) => axios.put(`${API}/draft-events/${id}/pause`),
  complete: (id) => axios.put(`${API}/draft-events/${id}/complete`),
  makePick: (id, data) => axios.post(`${API}/draft-events/${id}/pick`, data),
  autoPick: (id) => axios.post(`${API}/draft-events/${id}/auto-pick`),
  sendChat: (id, data) => axios.post(`${API}/draft-events/${id}/chat`, data),
  getChat: (id, limit = 50) => axios.get(`${API}/draft-events/${id}/chat`, { params: { limit } }),
  delete: (id) => axios.delete(`${API}/draft-events/${id}`),
  sendReaction: (id, reaction) => axios.post(`${API}/draft-events/${id}/react`, { reaction }),
};

// Trades API
export const tradesApi = {
  getAll: (limit = 50) => axios.get(`${API}/trades`, { params: { limit } }),
  getOne: (id) => axios.get(`${API}/trades/${id}`),
  execute: (data) => axios.post(`${API}/admin/trades`, data),
  delete: (id) => axios.delete(`${API}/admin/trades/${id}`),
};

// Membership API
export const memberApi = {
  selectTeam: (teamId) => axios.post(`${API}/member/select-team`, { team_id: teamId }),
  getProfile: () => axios.get(`${API}/member/profile`),
  getActivity: (teamId, limit = 30) => axios.get(`${API}/member/activity`, { params: { team_id: teamId, limit } }),
  getLeaderboard: (teamId, limit = 20) => axios.get(`${API}/member/leaderboard`, { params: { team_id: teamId, limit } }),
  getTeamHub: (teamId) => axios.get(`${API}/member/team-hub/${teamId}`),
  getMyVotes: () => axios.get(`${API}/member/my-votes`),
  getContent: (params = {}) => axios.get(`${API}/member/content`, { params }),
  getContentDetail: (id) => axios.get(`${API}/member/content/${id}`),
  // MEM-BADGE-1C — Record an authoritative Member content view.
  // Fire-and-forget from any content-detail page mount; a failure
  // must not disrupt the read flow. Advances `content_explorer`.
  recordContentView: (id) => axios.post(`${API}/member/content/${id}/view`),
  getFollowedProspects: () => axios.get(`${API}/member/followed-prospects`),
  // FE-15 — memberApi.getPerks and memberApi.getFeaturedContent were
  // orphaned by MemberDashboard retirement. Backend endpoints
  // (/api/member/perks + /api/member/content/featured) are preserved
  // for backend regression + future Content Hub work.
  startTrial: () => axios.post(`${API}/member/start-trial`),
  getTrialStatus: () => axios.get(`${API}/member/trial-status`),
  // MEM-BE-2B-P1C — server-authoritative Member visit ledger. Fire on
  // Member Dashboard mount. No body: identity + timezone + Member-date
  // are all resolved server-side from the auth token + profile.
  recordVisit: () => axios.post(`${API}/member/visit`),
  // MEM-BE-2B-P2C — authoritative retention summary read.
  // No client-controlled inputs (no user_id, no timezone, no date
  // range). Server derives all context from the auth token +
  // profile.timezone_iana + activity ledger.
  getRetentionSummary: () => axios.get(`${API}/member/retention-summary`),
  // MEM-BE-2B-P2A — authoritative zero-point observation events.
  // These do not award points, badges, or notifications. They exist
  // solely so the future retention-summary endpoint can derive daily
  // mission completion from the server-authoritative activity ledger.
  scoutProspect: (prospectId) =>
    axios.post(`${API}/member/scout-prospect/${prospectId}`),
  reviewTeamProgress: () =>
    axios.post(`${API}/member/team-progress-review`),
};

// Polls API
export const pollsApi = {
  getAll: (params = {}) => axios.get(`${API}/polls`, { params }),
  getOne: (id) => axios.get(`${API}/polls/${id}`),
  create: (data) => axios.post(`${API}/polls`, data),
  update: (id, data) => axios.put(`${API}/polls/${id}`, data),
  vote: (id, option) => axios.post(`${API}/polls/${id}/vote`, { selected_option: option }),
  // Phase 15 Phase 2 — advanced format responses + moderation
  respond: (id, payload) => axios.post(`${API}/polls/${id}/vote`, payload),
  hideFeedback: (pollId, responseId) =>
    axios.post(`${API}/polls/${pollId}/responses/${responseId}/hide`),
  close: (id) => axios.put(`${API}/polls/${id}/close`),
  pause: (id) => axios.put(`${API}/polls/${id}/pause`),
  resetVotes: (id) => axios.post(`${API}/polls/${id}/reset-votes`),
  removeOption: (id, optionValue) => axios.delete(`${API}/polls/${id}/options/${optionValue}`),
  getAnalytics: (id) => axios.get(`${API}/polls/${id}/analytics`),
  delete: (id) => axios.delete(`${API}/polls/${id}`),
  getResults: (id) => axios.get(`${API}/polls/${id}/results`),
  getVotingDashboard: () => axios.get(`${API}/admin/voting/dashboard`),
  // Phase 15 Phase 3 — moderation toggles (FO Staff / Team Admin)
  togglePin: (id) => axios.post(`${API}/polls/${id}/pin`),
  toggleOfficial: (id) => axios.post(`${API}/polls/${id}/official`),
};

// Discussions API
export const discussionsApi = {
  getAll: (params = {}) => axios.get(`${API}/discussions`, { params }),
  getOne: (id) => axios.get(`${API}/discussions/${id}`),
  create: (data) => axios.post(`${API}/discussions`, data),
  addComment: (id, content) => axios.post(`${API}/discussions/${id}/comments`, { content }),
  react: (id, reaction) => axios.post(`${API}/discussions/${id}/react`, { reaction }),
  // Phase 13 — admin pin/unpin toggle (also team-scoped for team_admin)
  togglePin: (id) => axios.post(`${API}/discussions/${id}/pin`),
  getCategories: () => axios.get(`${API}/discussions/categories`),
  // Phase 14 — moderation endpoints
  update: (id, data) => axios.put(`${API}/discussions/${id}`, data),
  remove: (id) => axios.delete(`${API}/discussions/${id}`),
  removeComment: (id, commentId) => axios.delete(`${API}/discussions/${id}/comments/${commentId}`),
  createCategory: (data) => axios.post(`${API}/discussions/categories`, data),
  updateCategory: (slug, data) => axios.put(`${API}/discussions/categories/${slug}`, data),
  deleteCategory: (slug) => axios.delete(`${API}/discussions/categories/${slug}`),
};

// League Admin API
export const leagueAdminApi = {
  getOverview: () => axios.get(`${API}/league-admin/overview`),
  // Travel
  getTravel: (params = {}) => axios.get(`${API}/league-admin/travel`, { params }),
  createTravel: (data) => axios.post(`${API}/league-admin/travel`, data),
  updateTravel: (id, data) => axios.put(`${API}/league-admin/travel/${id}`, data),
  deleteTravel: (id) => axios.delete(`${API}/league-admin/travel/${id}`),
  // Expenses
  getExpenses: (params = {}) => axios.get(`${API}/league-admin/expenses`, { params }),
  createExpense: (data) => axios.post(`${API}/league-admin/expenses`, data),
  approveExpense: (id) => axios.put(`${API}/league-admin/expenses/${id}/approve`),
  denyExpense: (id) => axios.put(`${API}/league-admin/expenses/${id}/deny`),
  reimburseExpense: (id) => axios.put(`${API}/league-admin/expenses/${id}/reimburse`),
  deleteExpense: (id) => axios.delete(`${API}/league-admin/expenses/${id}`),
  getExpenseSummary: () => axios.get(`${API}/league-admin/expenses/summary`),
  // Tasks
  getTasks: (params = {}) => axios.get(`${API}/league-admin/tasks`, { params }),
  createTask: (data) => axios.post(`${API}/league-admin/tasks`, data),
  updateTask: (id, data) => axios.put(`${API}/league-admin/tasks/${id}`, data),
  deleteTask: (id) => axios.delete(`${API}/league-admin/tasks/${id}`),
  // Games
  getGames: (params = {}) => axios.get(`${API}/league-admin/games`, { params }),
  updateGameOps: (id, data) => axios.put(`${API}/league-admin/games/${id}/ops`, data),
  // Team Operations
  getTeamOps: (id) => axios.get(`${API}/league-admin/teams/${id}/operations`),
  // Campaigns (Marketing)
  getCampaigns: (params = {}) => axios.get(`${API}/league-admin/campaigns`, { params }),
  createCampaign: (data) => axios.post(`${API}/league-admin/campaigns`, data),
  updateCampaign: (id, data) => axios.put(`${API}/league-admin/campaigns/${id}`, data),
  deleteCampaign: (id) => axios.delete(`${API}/league-admin/campaigns/${id}`),
  // Community Initiatives
  getInitiatives: (params = {}) => axios.get(`${API}/league-admin/initiatives`, { params }),
  createInitiative: (data) => axios.post(`${API}/league-admin/initiatives`, data),
  updateInitiative: (id, data) => axios.put(`${API}/league-admin/initiatives/${id}`, data),
  deleteInitiative: (id) => axios.delete(`${API}/league-admin/initiatives/${id}`),
  // Support Tickets (IT)
  getTickets: (params = {}) => axios.get(`${API}/league-admin/tickets`, { params }),
  createTicket: (data) => axios.post(`${API}/league-admin/tickets`, data),
  updateTicket: (id, data) => axios.put(`${API}/league-admin/tickets/${id}`, data),
  deleteTicket: (id) => axios.delete(`${API}/league-admin/tickets/${id}`),
  // Content
  getContent: (params = {}) => axios.get(`${API}/league-admin/content`, { params }),
  createContent: (data) => axios.post(`${API}/league-admin/content`, data),
  updateContent: (id, data) => axios.put(`${API}/league-admin/content/${id}`, data),
  deleteContent: (id) => axios.delete(`${API}/league-admin/content/${id}`),
  // Reports
  getReports: () => axios.get(`${API}/league-admin/reports`),
  // Admin Users
  // League Admin — admin user mgmt + full user directory
  getAdminUsers: () => axios.get(`${API}/league-admin/admin-users`),
  updateAdminUser: (id, data) => axios.put(`${API}/league-admin/admin-users/${id}`, data),
  // Phase 7Y — full user directory (League Admin only)
  listAllUsers: (params = {}) => axios.get(`${API}/league-admin/users`, { params }),
  // Phase 7Z — deactivate / reactivate (Prospect users only)
  deactivateUser: (uid, reason = null) =>
    axios.post(`${API}/league-admin/users/${uid}/deactivate`, { reason }),
  reactivateUser: (uid) =>
    axios.post(`${API}/league-admin/users/${uid}/reactivate`),
  // Phase 16 — Resend Member welcome email (members only)
  resendWelcomeEmail: (uid) =>
    axios.post(`${API}/league-admin/users/${uid}/resend-welcome-email`),
  // Coaching Staff
  getCoachingRoles: () => axios.get(`${API}/coaching-roles`),
  createCoachingStaff: (data) => axios.post(`${API}/league-admin/coaching-staff`, data),
  updateCoachingStaff: (id, data) => axios.put(`${API}/league-admin/coaching-staff/${id}`, data),
  deleteCoachingStaff: (id) => axios.delete(`${API}/league-admin/coaching-staff/${id}`),
  // Member Content
  getMemberContent: (params = {}) => axios.get(`${API}/league-admin/member-content`, { params }),
  createMemberContent: (data) => axios.post(`${API}/league-admin/member-content`, data),
  updateMemberContent: (id, data) => axios.put(`${API}/league-admin/member-content/${id}`, data),
  deleteMemberContent: (id) => axios.delete(`${API}/league-admin/member-content/${id}`),
};

// Ticker API
export const tickerApi = {
  getPicks: (limit = 20) => axios.get(`${API}/ticker/picks`, { params: { limit } }),
};

export const notificationsApi = {
  getAll: (params = {}) => axios.get(`${API}/notifications`, { params }),
  getUnreadCount: () => axios.get(`${API}/notifications/unread-count`),
  getActivityFeed: (params = {}) => axios.get(`${API}/activity-feed`, { params }),
  markRead: (id) => axios.put(`${API}/notifications/${id}/read`),
  markAllRead: () => axios.put(`${API}/notifications/read-all`),
  archive: (id) => axios.put(`${API}/notifications/${id}/archive`),
  archiveAllRead: () => axios.put(`${API}/notifications/archive-all-read`),
  delete: (id) => axios.delete(`${API}/notifications/${id}`),
  getPreferences: () => axios.get(`${API}/notifications/preferences`),
  updatePreferences: (data) => axios.put(`${API}/notifications/preferences`, data),
  adminSend: (data) => axios.post(`${API}/admin/notifications/send`, data),
  adminHistory: (params = {}) => axios.get(`${API}/admin/notifications/history`, { params }),
  adminAnalytics: () => axios.get(`${API}/admin/notifications/analytics`),
  scheduleNotification: (data) => axios.post(`${API}/admin/notifications/schedule`, data),
  getScheduled: () => axios.get(`${API}/admin/notifications/scheduled`),
  cancelScheduled: (id) => axios.delete(`${API}/admin/notifications/scheduled/${id}`),
  processScheduled: () => axios.post(`${API}/admin/notifications/process-scheduled`),
  getTemplates: () => axios.get(`${API}/admin/notification-templates`),
  createTemplate: (data) => axios.post(`${API}/admin/notification-templates`, data),
  updateTemplate: (id, data) => axios.put(`${API}/admin/notification-templates/${id}`, data),
  deleteTemplate: (id) => axios.delete(`${API}/admin/notification-templates/${id}`),
};

export const badgesApi = {
  getDefinitions: () => axios.get(`${API}/badges/definitions`),
  getCatalog:     () => axios.get(`${API}/badges/catalog`),
  getMyBadges:    () => axios.get(`${API}/member/badges`),
  getUserBadges:  (userId) => axios.get(`${API}/member/badges/${userId}`),
  evaluate:       () => axios.post(`${API}/member/badges/evaluate`),
};

// Membership API
export const membershipApi = {
  checkout: (originUrl) => axios.post(`${API}/membership/checkout`, { origin_url: originUrl }),
  status: (sessionId) => axios.get(`${API}/membership/status/${sessionId}`),
  myMembership: () => axios.get(`${API}/membership/my-membership`),
  cancel: () => axios.post(`${API}/membership/cancel`),
};

// WebSocket Helper
export const getWebSocketUrl = (path) => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const wsProtocol = backendUrl.startsWith('https') ? 'wss' : 'ws';
  const wsHost = backendUrl.replace(/^https?:\/\//, '');
  // WebSocket paths need /api prefix for ingress routing
  return `${wsProtocol}://${wsHost}/api${path}`;
};

export default {
  prospects: prospectsApi,
  teams: teamsApi,
  games: gamesApi,
  draftPool: draftPoolApi,
  draftEvents: draftEventsApi,
  mockDraft: mockDraftApi,
  votes: votesApi,
  leaderboards: leaderboardsApi,
  announcements: announcementsApi,
  stats: statsApi,
  follow: followApi,
  upload: uploadApi,
  ticker: tickerApi,
  trades: tradesApi,
  member: memberApi,
  polls: pollsApi,
  discussions: discussionsApi,
  leagueAdmin: leagueAdminApi,
  notifications: notificationsApi,
  badges: badgesApi,
  membership: membershipApi,
};

// AI Athlete Agent API
export const agentApi = {
  getMyInput: () => axios.get(`${API}/agent/my-input`),
  getMyAnalysis: () => axios.get(`${API}/agent/my-analysis`),
  getMyPlan: () => axios.get(`${API}/agent/my-plan`),
  getMyForecast: () => axios.get(`${API}/agent/my-forecast`),
  refreshMyForecast: () => axios.post(`${API}/agent/my-forecast/refresh`),
  getMyAdaptiveInput: () => axios.get(`${API}/agent/my-adaptive-input`),
  getMyNarratives: () => axios.get(`${API}/agent/my-narratives`),
  getMyCheckin: () => axios.get(`${API}/agent/my-checkin`),
  generateMyCheckin: (force = false) =>
    axios.post(`${API}/agent/my-checkin`, null, { params: force ? { force: true } : {} }),
  markCheckinRead: (checkinId) => axios.post(`${API}/agent/my-checkin/${checkinId}/read`),
  completeSession: (sessionId, body = {}) =>
    axios.post(`${API}/agent/my-plan/sessions/${sessionId}/complete`, body),
  updateSessionFeedback: (sessionId, body = {}) =>
    axios.patch(`${API}/agent/my-plan/sessions/${sessionId}/feedback`, body),
  getMyStreak: () => axios.get(`${API}/agent/my-streak`),
  getMyStreakHistory: (days = 30) =>
    axios.get(`${API}/agent/my-streak/history`, { params: { days } }),
  freezeMyStreakDay: (dateIso) =>
    axios.post(`${API}/agent/my-streak/freeze`, { date: dateIso }),
  getMyRootCause: () => axios.get(`${API}/agent/my-root-cause`),
  refreshMyRootCause: () =>
    axios.post(`${API}/agent/my-root-cause`, null, { params: { force: true } }),
  getMyPlanAdjustment: () => axios.get(`${API}/agent/my-plan-adjustment`),
  refreshMyPlanAdjustment: () =>
    axios.post(`${API}/agent/my-plan-adjustment`, null, { params: { force: true } }),
  runSetup: () => axios.post(`${API}/agent/my-setup`),
  // Phase 1A — Athlete Intelligence Intake
  getMyIntake: () => axios.get(`${API}/agent/intake`),
  saveMyIntake: (payload) => axios.put(`${API}/agent/intake`, payload),
  submitMyIntake: () => axios.post(`${API}/agent/intake/submit`),
  // Phase 1B-5 — Competency Interpretation Engine (rule-based, no AI)
  getMyInterpretation: () => axios.get(`${API}/development-interpretation/me`),
  computeMyInterpretation: () => axios.post(`${API}/development-interpretation/compute`),
  // Phase 1C-1 — Athlete Competency Matching Engine (weighted, ranked focus areas)
  getMyFocusAreas: () => axios.get(`${API}/competencies/for-me`),
  // Phase 1D-1 — Training Blueprint Engine (read-only 4-week scaffolding)
  getMyBlueprint: () => axios.get(`${API}/training/blueprint/me`),
  regenerateMyBlueprint: () => axios.post(`${API}/training/blueprint/regenerate`),
  // Phase 1D-3 — Daily Session Assembly (cached per user+day)
  getTodaySession: () => axios.get(`${API}/training/session/today`),
  regenerateTodaySession: () => axios.post(`${API}/training/session/regenerate`),
  // Phase 1D-5 — Workout Logging & Athlete Feedback System
  createTrainingLog: (payload) => axios.post(`${API}/training/logs`, payload),
  listTrainingLogs: (params) => axios.get(`${API}/training/logs`, { params }),
  listTrainingLogsBySession: (sessionId) => axios.get(`${API}/training/logs/by-session/${sessionId}`),
  updateTrainingLog: (logId, patch) => axios.patch(`${API}/training/logs/${logId}`, patch),
  deleteTrainingLog: (logId) => axios.delete(`${API}/training/logs/${logId}`),
  // Phase 1D-6 — Adaptive Readiness Intelligence (rule-based, light)
  getMyReadiness: () => axios.get(`${API}/training/readiness`),
};

// Member Waitlist API (admin-only reads)
export const memberWaitlistApi = {
  list: (params = {}) => axios.get(`${API}/admin/member-waitlist`, { params }),
  stats: () => axios.get(`${API}/admin/member-waitlist/stats`),
  // Public Rochester-only submission (server decides the team).
  join: ({ email, source }) => axios.post(`${API}/member-waitlist`, { email, source }),
};

// Contact / Support API — public, no auth required (axios interceptor still
// attaches a bearer when one is present; backend uses get_optional_user).
export const contactApi = {
  submit: (data) => axios.post(`${API}/contact`, data),
};
